import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class BreadcrumbService {
    private router = inject(Router);

    breadcrumbsSubject = new BehaviorSubject<{ id: string; label: string; link: string }[]>([]);
    breadcrumbs$ = this.breadcrumbsSubject.asObservable();

    constructor() {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            const breadcrumbs = this.createBreadcrumbs(this.router.routerState.snapshot.root);
            this.breadcrumbsSubject.next(breadcrumbs);
        });
    }
    private resolveRouteLabel(routeURL: string, routeParams: Record<string, string>): string {
        // Check if the routeURL contains ':'
        if (routeURL.includes(':')) {
            // Extract the key from the routeURL (e.g., ':id' -> 'id')
            const key = routeURL.split(':')[1];
            // Return the value from routeParams corresponding to the key
            return routeParams[key];
        } else {
            // If no ':' is found, return the routeURL as is
            return routeURL;
        }
    }

    private resolveRouteURL(routeURL, routeParams) {
        // Use a regular expression to find all occurrences of :param in the routeURL
        return routeURL.replace(
            /:([^/]+)/g,
            (_, key) =>
                // Replace each occurrence with the corresponding value from routeParams
                routeParams[key]
        );
    }

    private createBreadcrumbs(
        route: ActivatedRouteSnapshot,
        url = '',
        breadcrumbs: { id: string; label: string; link: string }[] = []
    ): { id: string; label: string; link: string }[] {
        const children: ActivatedRouteSnapshot[] = route.children;

        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            if (child.routeConfig && child.routeConfig.path) {
                const path: string = child.routeConfig.path;
                const routeParams = child.params;
                const resolvedLabel = this.resolveRouteLabel(path, routeParams);
                url += `/${path}`;
                const queryString = Object.keys(route.queryParams)
                    .map(
                        (key) =>
                            `${encodeURIComponent(key)}=${encodeURIComponent(route.queryParams[key])}`
                    )
                    .join('&');
                let resolveUrl = this.resolveRouteURL(url, routeParams);
                resolveUrl += '?' + queryString;
                const label = child.data.label || resolvedLabel;

                breadcrumbs.push({
                    id: path,
                    label,
                    link: resolveUrl,
                });

                return this.createBreadcrumbs(child, url, breadcrumbs);
            }
        }

        return breadcrumbs;
    }
}
